import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Button } from "react-bootstrap";
import locationData from "../../countries.json";
import { Formik } from "formik";
import * as yup from "yup";
import { useUpdateUserProfileMutation } from "../../app/features/apiSlice";
import { toastError, toastSuccess } from "../../helpers";

const DomicileDetails = ({ data, isLoading, error, refetch }) => {
    const [countries, setCountries] = useState([]);
    const { t } = useTranslation();

    const initialValues = {
        address_home: data?.user?.orgUser?.address_home || "",
        province_home: data?.user?.orgUser?.province_home || "",
        city_home: data?.user?.orgUser?.city_home || "",
        postcode_home: data?.user?.orgUser?.postcode_home || "",
        sameAsResidence: false,
    };

    const schema = yup.object().shape({
        address_home: yup.string().required("domicileAddressRequired"),
        province_home: yup.string().required("domicileCountryRequired"),
        city_home: yup.string().required("domicileCityRequired"),
        postcode_home: yup.string().required("domicilePostcodeRequired"),
    });

    const [updateUser, { isLoading: isUpdating }] = useUpdateUserProfileMutation();

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                address_home: values.address_home,
                province_home: values.province_home,
                city_home: values.city_home,
                postcode_home: values.postcode_home,
            };

            const response = await updateUser(payload).unwrap();
            const message = response?.message ?? "Domicile details updated successfully";
            toastSuccess(message);
            await refetch();
        } catch (err) {
            const errorMessage = err?.data?.message || err?.message || t("error.serverNotResponding");
            toastError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const countryData = locationData.map((country) => country.name);
        setCountries(countryData);
    }, []);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        console.error("Data loading error:", error);
        return <div>Error loading domicile details</div>;
    }

    return (
        <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
        >
            {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                errors,
                isSubmitting,
                setFieldValue,
            }) => {
                const handleSameAsResidence = (checked) => {
                    if (checked) {
                        setFieldValue(
                            "address_home",
                            data?.user?.orgUser?.address || ""
                        );
                        setFieldValue(
                            "province_home",
                            data?.user?.orgUser?.province || ""
                        );
                        setFieldValue(
                            "city_home",
                            data?.user?.orgUser?.city || ""
                        );
                        setFieldValue(
                            "postcode_home",
                            data?.user?.orgUser?.postcode || ""
                        );
                    } else {
                        setFieldValue("address_home", "");
                        setFieldValue("province_home", "");
                        setFieldValue("city_home", "");
                        setFieldValue("postcode_home", "");
                    }
                };

                return (
                    <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="add-inner-form"
                    >
                        <h6 className="text-danger bold">
                            You can request changes to this information by writing to
                            support@excelior.ch
                        </h6>

                        {/* Same as residence */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                label={t("sameAsDomicile")}
                                name="sameAsResidence"
                                checked={values.sameAsResidence}
                                onChange={(e) => {
                                    handleChange(e);
                                    handleSameAsResidence(e.target.checked);
                                }}
                            />
                        </Form.Group>

                        <Row>
                            {/* Address */}
                            <Form.Group
                                as={Col}
                                xs="12"
                                md="6"
                                className="mb-3"
                            >
                                <Form.Label>{t("form.address")} *</Form.Label>

                                <Form.Control
                                    type="text"
                                    name="address_home"
                                    placeholder={t("form.address")}
                                    value={values.address_home}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                        touched.address_home && !!errors.address_home
                                    }
                                    size="sm"
                                />

                                <Form.Control.Feedback type="invalid">
                                    {t(`error.${errors.address_home}`)}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Country */}
                            <Form.Group
                                as={Col}
                                xs="12"
                                md="6"
                                className="mb-3"
                            >
                                <Form.Label>{t("form.country")} *</Form.Label>

                                <Form.Select
                                    name="province_home"
                                    value={values.province_home}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                        touched.province_home &&
                                        !!errors.province_home
                                    }
                                    size="sm"
                                >
                                    <option value="">
                                        {t("form.select")}
                                    </option>

                                    {countries.map((country, i) => (
                                        <option key={i} value={country}>
                                            {country}
                                        </option>
                                    ))}
                                </Form.Select>

                                <Form.Control.Feedback type="invalid">
                                    {t(`error.${errors.province_home}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>

                        <Row>
                            {/* City */}
                            <Form.Group
                                as={Col}
                                xs="12"
                                md="6"
                                className="mb-3"
                            >
                                <Form.Label>{t("form.city")} *</Form.Label>

                                <Form.Control
                                    type="text"
                                    name="city_home"
                                    placeholder={t("form.city")}
                                    value={values.city_home}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                        touched.city_home && !!errors.city_home
                                    }
                                    size="sm"
                                />

                                <Form.Control.Feedback type="invalid">
                                    {t(`error.${errors.city_home}`)}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Postcode */}
                            <Form.Group
                                as={Col}
                                xs="12"
                                md="6"
                                className="mb-3"
                            >
                                <Form.Label>{t("form.postcode")} *</Form.Label>

                                <Form.Control
                                    type="text"
                                    name="postcode_home"
                                    placeholder={t("form.postcode")}
                                    value={values.postcode_home}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                        touched.postcode_home &&
                                        !!errors.postcode_home
                                    }
                                    size="sm"
                                />

                                <Form.Control.Feedback type="invalid">
                                    {t(`error.${errors.postcode_home}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>

                        <Button
                            variant="primary-outline"
                            type="submit"
                            disabled={isSubmitting || isUpdating}
                        >
                            {t("update")}
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default DomicileDetails;